import React, { Component, createRef } from 'react';
import { array, bool, number, object, string } from 'prop-types';
import { connect } from 'react-redux';
import { getContext } from '@fiverr-private/fiverr_context';
import { metric } from '@fiverr-private/perseus';
import { shouldDisplayMigrationPopup } from '@fiverr-private/business_blocks';
import {
    isMilestonesOrderSelected,
    isRecurringOrderSelected
} from '../../../reducers/checkoutPlan';
import { MP_CLICKED_PACKAGE_SELECT } from '../../../utils/mixPanelEvents';
import { BQ_CLICKED_PACKAGE_SELECT, BQ_GIG_PAGE_WITHOUT_EXTRAS } from '../../../utils/bigQueryEvents';
import { ORDER_NOW_BUTTON_CLICKS } from '../../../utils/sendGraphiteEvent/events';
import { sendGraphiteEvent } from '../../../utils/sendGraphiteEvent';
import { isFiverrChoice } from '../../../utils/fiverrChoice';
import { isRisingTalent } from '../../../utils/risingTalent';
import { triggerFibMigrationPopup } from '../../../utils/fibMigrationPopup';
import { PACKAGE_POSITIONS } from '../../../types/packagePositions';
import {
    getNotIncludedFeatures,
    getPackageById,
    getSelectedRecurringOption,
    isSinglePackage
} from '../../../reducers/packages';
import PaymentFormItems from './PaymentFormItems';
import CheckoutFormItems from './CheckoutFormItems';
import { packageHasExtras, shouldSkipCheckout, shouldTriggerLoginModal } from './utils';
import { CHECKOUT_TRIGGER, OPEN_SIGNIN_MODAL_METRIC, SUCCESSFUL_SIGNIN_METRIC } from './constants';

class CheckoutFormHOC extends Component {

    constructor() {
        super();

        this.checkoutFormRef = createRef();
        this.onSubmit = this.onSubmit.bind(this);
        this.afterSigningSuccess = this.afterSigningSuccess.bind(this);
    }

    componentDidMount() {
        this.reportPackageWithoutExtrasSelected();
    }

    componentDidUpdate(prevProps) {
        if (this.props.selectedPackageId !== prevProps.selectedPackageId) {
            this.reportPackageWithoutExtrasSelected();
        }
    }

    reportPackageWithoutExtrasSelected() {
        const sources = [PACKAGE_POSITIONS.TOP_RIGHT, PACKAGE_POSITIONS.TABS];
        const {
            selectedPackageData,
            notIncludedFeatures,
            selectedPackageId,
            source
        } = this.props;
        const { biEvents } = this.context;

        if (!selectedPackageData || !notIncludedFeatures) {
            return;
        }

        const noExtras = !packageHasExtras({ selectedPackageData, notIncludedFeatures });
        const params = {
            packageId: selectedPackageId
        };

        if (noExtras && sources.includes(source)) {
            biEvents.sendBigQueryEvent({ eventName: BQ_GIG_PAGE_WITHOUT_EXTRAS, params });
        }

    }

    reportBI() {
        const {
            selectedPackageId,
            selectedRecurringOption,
            source,
            isRecurringOrderSelected
        } = this.props;
        const { biEvents } = this.context;

        const params = {
            id: selectedPackageId,
            source
        };

        if (isRecurringOrderSelected) {
            params.selectedRecurringOption = selectedRecurringOption;
        }

        biEvents.sendMixPanelEvent({ eventName: MP_CLICKED_PACKAGE_SELECT, params });
        biEvents.sendBigQueryEvent({ eventName: BQ_CLICKED_PACKAGE_SELECT, params });

        sendGraphiteEvent(ORDER_NOW_BUTTON_CLICKS);
    }

    onSubmit(event) {
        this.reportBI();
        const { selectedPackageData, notIncludedFeatures } = this.props;
        if (shouldDisplayMigrationPopup()){
            event.preventDefault();
            const formSubmit = () => this.checkoutFormRef.current.submit();
            triggerFibMigrationPopup(CHECKOUT_TRIGGER, formSubmit);
        } else if (shouldTriggerLoginModal({ selectedPackageData, notIncludedFeatures })) {
            event.preventDefault();
            metric.count(OPEN_SIGNIN_MODAL_METRIC);
            window.openSignInModal({ afterSigningSuccess: this.afterSigningSuccess });
        }
    }

    afterSigningSuccess() {
        metric.count(SUCCESSFUL_SIGNIN_METRIC);
        this.checkoutFormRef.current.submit();
    }

    isFiverrChoice() {
        const { choiceEligibilities } = this.context;
        const { queryParameters, abTests } = getContext();

        return isFiverrChoice({ queryParameters, abTests, choiceEligibilities });
    }

    isRisingTalent() {
        const { general } = this.context;
        const { isHandpicked } = general;

        return isRisingTalent(isHandpicked);
    }

    render() {
        const {
            selectedPackageId,
            selectedRecurringOption,
            isRecurringOrderSelected,
            isMilestonesOrderSelected,
            children,
            isSingle,
            quantity,
            className,
            selectedPackageData,
            notIncludedFeatures,
            duration
        } = this.props;

        const { pathfinderWrapper, general } = this.context;
        const { gigId } = general;
        const { csrfToken, isMobile } = getContext();
        const isFiverrChoice = this.isFiverrChoice();
        const isRisingTalent = !isFiverrChoice && this.isRisingTalent();
        const skipCheckout = shouldSkipCheckout({ selectedPackageData, notIncludedFeatures });

        const checkoutAction = isMobile ? 'checkout_package' : 'checkout_customize';
        const formAction = skipCheckout ?
            pathfinderWrapper.pathfinder('payments') :
            pathfinderWrapper.pathfinder(checkoutAction, { gig_id: gigId });

        const sharedFormItemsProps = {
            quantity,
            isRisingTalent,
            isFiverrChoice,
            isRecurringOrderSelected,
            selectedPackageId,
            selectedRecurringOption,
            gigId,
            isMilestoneOrder: isMilestonesOrderSelected
        };

        return (
            <form className={className} action={formAction}
                method="post" onSubmit={this.onSubmit} ref={this.checkoutFormRef}>
                <input type="hidden" name="authenticity_token" value={csrfToken}/>
                {skipCheckout ?
                    <PaymentFormItems {...sharedFormItemsProps} duration={duration}/> :
                    <CheckoutFormItems {...sharedFormItemsProps} isSingle={isSingle}/>
                }
                {
                    React.Children.map(children, (child) => {
                        if (child !== null) {
                            return React.cloneElement(child, {});
                        }
                    })
                }
            </form>
        );
    }
}

const mapStateToProps = (state, { selectedPackageId, source, className }) => {
    const { packages } = state;
    const selectedPackageData = getPackageById(packages, selectedPackageId);
    const { duration } = selectedPackageData;

    return ({
        selectedPackageId,
        isRecurringOrderSelected: isRecurringOrderSelected(state),
        isMilestonesOrderSelected: isMilestonesOrderSelected(state),
        selectedRecurringOption: getSelectedRecurringOption(packages),
        source,
        className,
        selectedPackageData,
        duration,
        isSingle: isSinglePackage(packages),
        quantity: packages.quantity,
        notIncludedFeatures: getNotIncludedFeatures(state)
    });
};

CheckoutFormHOC.propTypes = {
    selectedPackageId: number.isRequired,
    isRecurringOrderSelected: bool,
    isMilestonesOrderSelected: bool,
    selectedRecurringOption: object,
    source: string.isRequired,
    isSingle: bool,
    quantity: number,
    className: string,
    selectedPackageData: object,
    notIncludedFeatures: array,
    duration: number
};

CheckoutFormHOC.contextTypes = {
    biEvents: object,
    pathfinderWrapper: object,
    general: object,
    choiceEligibilities: array
};

export { CheckoutFormHOC };

export default connect(mapStateToProps)(CheckoutFormHOC);
